import React from 'react';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

export interface SectionCardProps {
  title: string;
  icon?: React.ReactNode;
  /** `upper` (default) renders the label in caps; `sentence` keeps the string as written. */
  titleCase?: 'upper' | 'sentence';
  /** Extra classes on the outer surface (e.g. `flex-1` to stretch inside a flex column). */
  className?: string;
  children: React.ReactNode;
}

/**
 * Titled surface container grouping related content under a section label with an optional icon.
 */
export function SectionCard({ title, icon, titleCase = 'upper', className, children }: SectionCardProps) {
  const upper = titleCase === 'upper';
  return (
    <section
      className={cn(
        'rounded-(--radius-lg) border border-(--border-subtle)',
        'bg-(--surface-primary) p-[var(--space-4)] shadow-[var(--shadow-sm)]',
        className,
      )}
    >
      <div className="flex items-center gap-[var(--space-2)] mb-[var(--space-3)]">
        {icon && <Text component="span" className="text-(--text-tertiary)">{icon}</Text>}
        <Text
          className={cn(
            upper
              ? 'font-bold text-[length:var(--text-meta)] uppercase tracking-wider text-(--text-tertiary)'
              : 'text-sm [font-weight:var(--font-bold)] text-(--text-primary)',
          )}
        >
          {title}
        </Text>
      </div>
      {children}
    </section>
  );
}
